import React from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

export const ThemeTest: React.FC = () => {
  const { currentTheme, themeSettings, setTheme, canManageTheme } = useTheme();

  const handleThemeChange = (themeId: string) => {
    setTheme(themeId);
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Theme Test Component</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold mb-2">Current Theme: {currentTheme.name}</h3>
            <p className="text-muted-foreground">{currentTheme.description}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <h4 className="font-medium">Primary Colors</h4>
              <div className="flex gap-2">
                <div 
                  className="w-8 h-8 rounded border"
                  style={{ backgroundColor: currentTheme.colors.primary }}
                />
                <div 
                  className="w-8 h-8 rounded border"
                  style={{ backgroundColor: currentTheme.colors.primaryHover }}
                />
                <div 
                  className="w-8 h-8 rounded border"
                  style={{ backgroundColor: currentTheme.colors.primaryLight }}
                />
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Secondary Colors</h4>
              <div className="flex gap-2">
                <div 
                  className="w-8 h-8 rounded border"
                  style={{ backgroundColor: currentTheme.colors.secondary }}
                />
                <div 
                  className="w-8 h-8 rounded border"
                  style={{ backgroundColor: currentTheme.colors.secondaryHover }}
                />
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Accent Colors</h4>
              <div className="flex gap-2">
                <div 
                  className="w-8 h-8 rounded border"
                  style={{ backgroundColor: currentTheme.colors.accent }}
                />
                <div 
                  className="w-8 h-8 rounded border"
                  style={{ backgroundColor: currentTheme.colors.accentHover }}
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-medium">Theme-Aware Components</h4>
            
            <div className="space-y-2">
              <Button>Primary Button</Button>
              <Button variant="secondary">Secondary Button</Button>
              <Button variant="outline">Outline Button</Button>
            </div>

            <div className="space-y-2">
              <Input placeholder="Theme-aware input field" />
            </div>

            <div className="p-4 border rounded-lg">
              <p className="text-primary">This text uses primary color</p>
              <p className="text-secondary">This text uses secondary color</p>
              <p className="bg-accent p-2 rounded">This has accent background</p>
            </div>
          </div>

          {canManageTheme && (
            <div className="space-y-4">
              <h4 className="font-medium">Theme Switcher</h4>
              <div className="flex flex-wrap gap-2">
                {themeSettings.themes.map((theme) => (
                  <Button
                    key={theme.id}
                    variant={currentTheme.id === theme.id ? "default" : "outline"}
                    onClick={() => handleThemeChange(theme.id)}
                    className="text-xs"
                  >
                    {theme.name}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {!canManageTheme && (
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-muted-foreground">
                Only admins can change themes. Current user role doesn't have permission.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}; 